/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { FieldArray } from "formik";
import PropTypes from "prop-types";

// Material UI
import { makeStyles } from "@material-ui/core/styles";

// Actions
import {
  deleteMedia,
  getMediaItem,
  clearMediaError,
  getMedia
} from "src/state/mediaPerpetrators/actions";

// Config
import { mediaCategory } from "src/config/values";
import colors from "../../../config/colors";
import { useAppInsights } from "src/utils/telemetry/AppInsights";

// Components
import AddPerpetratorWrapper from "src/containers/CaseIncidents/components/AddPerpetratorWrapper";
import InputListHandler from "./InputListHandler";
import InputListItem from "./InputListItem";
import LoadingSection from "src/components/LoadingSection";
import MediaPerpetratorsItemEdit from "src/containers/CaseIncidents/components/MediaPerpetratorsItemEdit";
import MediaReuploadProvider from "src/components/MediaItemList/MediaReuploadProvider";
import Text from "../../Text";
import UserAddition from "../../UserAddition";

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(1),
    flex: "1 1",
    display: "flex",
    flexWrap: "wrap"
  },
  title: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1)
  },
  contentEdit: {
    display: "flex",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: colors.whisper,
    color: colors.santasGray
  },
  addButton: {
    display: "block",
    marginLeft: "auto",
    border: "none",
    "&:hover": {
      border: "none"
    }
  },
  input: {
    position: "relative"
  },
  close: {
    position: "absolute",
    right: 0,
    top: -10,
    cursor: "pointer"
  },
  info: {
    position: "absolute",
    right: 0,
    top: 3
  },
  valueLabel: {
    marginBottom: theme.spacing(1.5)
  },
  fields: {
    width: "100%"
  }
}));

const InputListPerpetrators = ({
  addButtonText,
  canAddMore = true,
  className,
  constantValues,
  disableIndex,
  name,
  onDelete,
  subTitle,
  title,
  type,
  values,
  setFieldValue,
  idArray,
  showDelete,
  ...rest
}) => {
  const [isRemoving, setRemoving] = useState(null);
  const [localArray, setLocalArray] = useState([]);
  const { caseId } = useParams();
  const dispatch = useDispatch();
  const { trackException } = useAppInsights();
  const classes = useStyles();
  const { currentCase, data, isLoading, isDeleting, error } = useSelector(
    state => state.mediaPerpetrators
  );

  useEffect(() => {
    return () => {
      dispatch(clearMediaError());
    };
  }, [dispatch]);

  useEffect(() => {
    if ((currentCase !== caseId || !data) && !error && !isLoading) {
      dispatch(
        getMedia({
          caseId,
          category: mediaCategory.PERPETRATOR,
          onFail: () =>
            trackException(`Failed to get media from case: ${caseId}`)
        })
      );
    }
  }, [caseId, currentCase, data, dispatch, error, isLoading, trackException]);

  const handleRemove = useCallback(
    (index, callback) => () => {
      if (index >= idArray.length) {
        callback();
        setLocalArray([...idArray, ...localArray].splice(index, 1));
        return;
      }

      if (!onDelete) {
        callback();
      } else {
        const itemId = idArray[index];
        // parent component expects the id of the deleted element
        // to use it on an async function
        setRemoving(index);

        onDelete(itemId, success => {
          if (success) {
            // remove deleted input element:
            callback();
            // remove deleted id element:
            setFieldValue(
              `editingId.${name}`,
              idArray.filter(id => id !== itemId)
            );
          }
          setRemoving(null);
        });
      }
    },
    [onDelete, setFieldValue, idArray]
  );

  const handleAdd = cbk => () => {
    localArray.push(new Date() * Math.random());
    cbk();
  };

  const generateKey = i => idArray[i] || [...idArray, ...localArray][i] || i;

  const handleDelete = itemId => () =>
    dispatch(
      deleteMedia({
        itemId,
        caseId,
        onFail: () =>
          trackException(
            `Failed to delete perpetrator item: ${itemId} at case: ${caseId}`
          )
      })
    );
  const handleFetchImage = mediaId => () => {
    dispatch(
      getMediaItem({
        caseId,
        mediaId,
        onFail: () =>
          trackException(
            `Failed to get media item: ${mediaId} at case: ${caseId}`
          )
      })
    );
  };

  const hasMediaPerpetrators = data && data.length > 0;
  const showAddPerpetrator = !values && !hasMediaPerpetrators;

  if (isLoading) return <LoadingSection normalHeight noBackground />;

  return (
    <div className={className}>
      <Text className={classes.title} type="heading4">
        {title}
      </Text>
      <div className={classes.root}>
        <FieldArray
          name={name}
          render={arrayHelpers => (
            <InputListHandler
              values={values}
              addItem={handleAdd(() => arrayHelpers.push(""))}
            >
              <div className={classes.fields}>
                {constantValues &&
                  constantValues.map(({ text, creationDateTime, author }) => (
                    <div
                      className={classes.input}
                      key={`${author.id}${creationDateTime}${text}`}
                    >
                      <UserAddition
                        date={creationDateTime}
                        author={author}
                        text={text}
                      />
                    </div>
                  ))}
                {values &&
                  values.map(
                    (_, index) =>
                      !disableIndex[index] && (
                        <InputListItem
                          onRemove={handleRemove}
                          removeCallback={arrayHelpers.remove}
                          key={generateKey(index)}
                          index={index}
                          disableDelete={!!isRemoving}
                          isLoading={isRemoving === index}
                          disabled={idArray[index] !== undefined}
                          name={`${name}.${index}`}
                          type={type}
                          {...rest}
                        />
                      )
                  )}
                <MediaReuploadProvider page="incidentPage" caseId={caseId}>
                  {hasMediaPerpetrators &&
                    data.map(item => (
                      <MediaPerpetratorsItemEdit
                        text={rest.label}
                        item={item}
                        isDeleting={isDeleting}
                        key={item.id}
                        showDelete={showDelete}
                        onDelete={handleDelete(item.id)}
                        onFetchImage={handleFetchImage}
                      />
                    ))}
                </MediaReuploadProvider>
                {canAddMore && (
                  <AddPerpetratorWrapper open={showAddPerpetrator} />
                )}
              </div>
            </InputListHandler>
          )}
        />
      </div>
    </div>
  );
};

InputListPerpetrators.propTypes = {
  addButtonText: PropTypes.string.isRequired,
  canAddMore: PropTypes.bool,
  className: PropTypes.string,
  constantValues: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      creationDateTime: PropTypes.string,
      author: PropTypes.shape({
        id: PropTypes.string,
        fullName: PropTypes.string
      })
    })
  ),
  disableIndex: PropTypes.arrayOf(PropTypes.bool),
  idArray: PropTypes.arrayOf(PropTypes.string),
  name: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  setFieldValue: PropTypes.func.isRequired,
  subTitle: PropTypes.string,
  title: PropTypes.string.isRequired,
  type: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
  showDelete: PropTypes.bool.isRequired
};

InputListPerpetrators.defaultProps = {
  canAddMore: false,
  className: "",
  constantValues: [],
  disableIndex: [],
  idArray: [],
  onDelete: () => {},
  subTitle: undefined,
  type: "text"
};

export default React.memo(InputListPerpetrators);
